import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import {
  Download, Upload, Eye, Share2, UserPlus, ShieldAlert, Search,
  Clock, FileDown, Calendar, X,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INITIAL_FILTERS = {
  action: "",
  entity_type: "",
  user_name: "",
  date_from: "",
  date_to: "",
};

const ACTION_OPTIONS = [
  "login", "upload", "download", "view", "share", "create", "update", "anonymize",
];

const ENTITY_OPTIONS = ["patient", "study", "file", "share_link", "user"];

const actionIcons = {
  upload: Upload, download: Download, view: Eye, share: Share2,
  create: UserPlus, anonymize: ShieldAlert, update: UserPlus, seed: FileDown,
  login: UserPlus,
};
const actionColors = {
  upload: "bg-sky-50 text-sky-700", download: "bg-teal-50 text-teal-700",
  view: "bg-amber-50 text-amber-700", share: "bg-violet-50 text-violet-700",
  create: "bg-cyan-50 text-cyan-700", anonymize: "bg-red-50 text-red-700",
  update: "bg-teal-50 text-teal-700", seed: "bg-slate-100 text-slate-600",
  login: "bg-indigo-50 text-indigo-700",
};

const buildAuditParams = (filters) => {
  const params = {};
  if (filters.action) params.action = filters.action;
  if (filters.entity_type) params.entity_type = filters.entity_type;
  if (filters.user_name) params.user_name = filters.user_name;
  if (filters.date_from) params.date_from = filters.date_from;
  if (filters.date_to) params.date_to = filters.date_to;
  return params;
};

const hasActiveFilters = (filters) =>
  Object.values(filters).some((v) => v !== "" && v != null);

export default function AuditTrail() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [debouncedFilters, setDebouncedFilters] = useState(INITIAL_FILTERS);
  const [page, setPage] = useState(1);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedFilters(filters), 400);
    return () => clearTimeout(timer);
  }, [filters]);

  const updateFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 30, ...buildAuditParams(debouncedFilters) };
      const res = await axios.get(`${API}/audit`, { params });
      setLogs(res.data.logs || []);
      setTotal(res.data.total ?? 0);
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || "Failed to load audit logs");
    } finally {
      setLoading(false);
    }
  }, [debouncedFilters, page]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const clearFilters = () => {
    setFilters(INITIAL_FILTERS);
    setPage(1);
  };

  const exportLogs = async () => {
    setExporting(true);
    try {
      const params = buildAuditParams(filters);
      const res = await axios.get(`${API}/audit/export`, {
        params,
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "audit_trail.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || "Failed to export audit logs");
    } finally {
      setExporting(false);
    }
  };

  const filtersActive = useMemo(() => hasActiveFilters(filters), [filters]);

  return (
    <div className="space-y-4" data-testid="audit-trail-page">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 flex-1 w-full min-w-0">
          <div className="relative w-full sm:w-44 shrink-0">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <Input
              data-testid="filter-user"
              placeholder="Search by user..."
              value={filters.user_name}
              onChange={(e) => updateFilter("user_name", e.target.value)}
              className="pl-8 h-9"
            />
          </div>

          <div className="flex items-center gap-2 flex-wrap">
          <Select
            value={filters.action || "all"}
            onValueChange={(v) => updateFilter("action", v === "all" ? "" : v)}
          >
            <SelectTrigger data-testid="filter-action" className="w-full sm:w-32 h-9">
              <SelectValue placeholder="Action" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Actions</SelectItem>
              {ACTION_OPTIONS.map((a) => (
                <SelectItem key={a} value={a} className="capitalize">{a}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={filters.entity_type || "all"}
            onValueChange={(v) => updateFilter("entity_type", v === "all" ? "" : v)}
          >
            <SelectTrigger data-testid="filter-entity" className="w-full sm:w-32 h-9">
              <SelectValue placeholder="Entity" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Entities</SelectItem>
              {ENTITY_OPTIONS.map((e) => (
                <SelectItem key={e} value={e} className="capitalize">{e.replace("_", " ")}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Calendar className="w-3.5 h-3.5 flex-shrink-0" />
              <Input
                data-testid="filter-date-from"
                type="date"
                value={filters.date_from}
                onChange={(e) => updateFilter("date_from", e.target.value)}
                className="h-9 w-full sm:w-36 text-xs"
                aria-label="From date"
              />
            </div>
            <span className="hidden sm:inline text-xs text-muted-foreground">to</span>
            <Input
              data-testid="filter-date-to"
              type="date"
              value={filters.date_to}
              onChange={(e) => updateFilter("date_to", e.target.value)}
              className="h-9 w-full sm:w-36 text-xs"
              aria-label="To date"
            />
          </div>

          {filtersActive ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearFilters}
              data-testid="clear-audit-filters"
              className="text-xs gap-1"
            >
              <X className="w-3.5 h-3.5" /> Clear
            </Button>
          ) : null}
          </div>
        </div>

        <div className="flex items-center justify-between sm:justify-end gap-2 w-full sm:w-auto">
          <span className="text-sm text-muted-foreground">{total} entries</span>
          <Button
            variant="outline"
            size="sm"
            onClick={exportLogs}
            disabled={exporting}
            data-testid="export-audit"
            className="gap-1 text-xs"
          >
            <FileDown className="w-3.5 h-3.5" />
            {exporting ? "Exporting…" : "Export CSV"}
          </Button>
        </div>
      </div>

      <Card className="overflow-hidden">
        <div className="overflow-auto h-[calc(100vh-220px)]">
          <Table className="sticky-table min-w-[640px]">
            <TableHeader>
              <TableRow>
                <TableHead className="w-44">Timestamp</TableHead>
                <TableHead className="w-24">Action</TableHead>
                <TableHead className="w-24">Entity</TableHead>
                <TableHead className="w-36">User</TableHead>
                <TableHead>Details</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">Loading...</TableCell></TableRow>
              ) : logs.length === 0 ? (
                <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">No audit logs found</TableCell></TableRow>
              ) : logs.map((log, i) => {
                const Icon = actionIcons[log.action] || Eye;
                const colorClass = actionColors[log.action] || "bg-zinc-500/10 text-zinc-400";
                return (
                  <TableRow key={log.id || i} data-testid={`audit-row-${i}`}>
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      <span className="flex items-center gap-1.5">
                        <Clock className="w-3 h-3" />
                        {new Date(log.timestamp).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge className={`gap-1 text-[10px] capitalize ${colorClass}`}>
                        <Icon className="w-3 h-3" /> {log.action}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-[10px] capitalize">{log.entity_type?.replace("_", " ")}</Badge>
                    </TableCell>
                    <TableCell className="text-sm font-medium">{log.user_name}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{log.details}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}
